import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import { Badge, Space, Table, Tabs } from 'antd';
import useForm from '../../hooks/useForm';
import PageTitle from '../PageTitle';
import FormWrapper from '../FormWrapper';
import SaveButton from '../buttons/SaveButton';
import CancelButton from '../buttons/CancelButton';
import Column from '../../helpers/columns';
import useDatasource from '../../hooks/useDatasource';
import { searchReports } from '../../services/reports/reports';
import { searchCouncilDecisions } from '../../services/taxonomies/council-decision';
import usePermissions from '../../hooks/usePermission';

const permissionsMap = {
  canEdit: ['SYS_SUPERUSER', 'TAXONOMIES_PERMISSION'],
};

const EditCouncilDecisionForm = ({
  councilDecisionReport,
  onSubmit,
  onCancel,
}) => {
  const { t } = useTranslation();
  const { canEdit } = usePermissions(permissionsMap);
  const validationSchema = useMemo(
    () =>
      yup.object().shape({
        startDate: yup.string().nullable().required(t('errors.notEmpty')),
        councilDecisionIds: yup
          .array()
          .nullable()
          .min(1, t('errors.councilDecisionIdsNotSelected'))
          .required('userTypes is required'),
        reports: yup
          .array()
          .nullable()
          .min(1, t('errors.councilDecisionIdsNotSelected'))
          .required('userTypes is required'),
      }),
    [t],
  );

  const selectionHandlerForField = (value, key, setValue, field) => ({
    selectedRowKeys: value || [],
    onSelect: (record, selected) =>
      selected
        ? setValue(key, [...(value || []), record[field]])
        : setValue(
            key,
            value.filter((item) => item !== record[field]),
          ),
    getCheckboxProps: (record) => ({
      disabled:
        record.deleted &&
        (value?.length > 0 ? value?.indexOf(record[field]) === -1 : true),
    }),
  });

  const { value, handleSubmit, custom, setValue, setFormValue, errorMessages } =
    useForm({
      initialValue: councilDecisionReport,
      schema: validationSchema,
      onSubmit,
      onCancel,
      validateOnChange: true,
    });

  const reportsSelection = useMemo(
    () => ({
      selectedRowKeys: value.reports,
      onSelect: ({ uri, label }) => {
        setValue('reports', [uri]);
        setValue('reportRef', uri);
        setValue('reportName', label);
      },
      getCheckboxProps: ({ deleted, uri }) => ({
        disabled:
          (deleted &&
            (value.reports?.length > 0
              ? value.reports?.indexOf(uri) === -1
              : true)) ||
          value.deleted,
      }),
    }),
    [setValue, value],
  );

  const councilDecisionSelection = useMemo(
    () =>
      selectionHandlerForField(
        value.councilDecisionIds,
        'councilDecisionIds',
        setValue,
        'id',
      ),
    [value.councilDecisionIds, setValue],
  );

  const reportsColumns = useMemo(
    () => [
      Column.text('label', t('entity.reports.label'), {
        width: 300,
      }),
    ],
    [t],
  );

  const councilDecisionColumns = useMemo(
    () => [
      Column.text('name', t('entity.taxonomy.councilDecision.name'), {
        filter: true,
      }),
      Column.date('startDate', t('entity.taxonomy.councilDecision.startDate'), {
        sort: true,
        filter: true,
        format: 'YYYY-MM-DD',
      }),
      Column.bool('deleted', t('common.deleted._'), {
        filter: true,
        inverted: true,
        labels: [
          t('table.filter.all'),
          t('common.deleted.true'),
          t('common.deleted.false'),
          t('table.filter.unknown'),
        ],
      }),
    ],
    [t],
  );

  const {
    pagination: reportsPagination,
    content: reportsTypes,
    handleChange: reportsHandleChange,
  } = useDatasource(searchReports);

  const {
    pagination: councilDecisionPagination,
    content: councilDecisionTypes,
    handleChange: councilDecisionHandleChange,
  } = useDatasource(searchCouncilDecisions);

  useEffect(() => {
    setFormValue(councilDecisionReport);
  }, [setFormValue, councilDecisionReport]);

  const { deleted } = value;

  return (
    <>
      <form onSubmit={handleSubmit}>
        <PageTitle
          title={
            councilDecisionReport.reportName ||
            t('entity.taxonomy.councilDecisionReport._singular')
          }
        />

        <FormWrapper>
          <FormWrapper.Single>
            <FormWrapper.DateTimePicker
              key="startDate"
              label={t(`entity.taxonomy.councilDecisionReport.startDate`)}
              props={{
                ...custom('startDate'),
                hideTime: true,
                disabled: deleted || !canEdit,
                toMidnightDown: true,
              }}
            />
            <FormWrapper.DateTimePicker
              key="endDate"
              label={t(`entity.taxonomy.councilDecisionReport.endDate`)}
              props={{
                ...custom('endDate'),
                hideTime: true,
                disabled: deleted || !canEdit,
                toMidnightUp: true,
              }}
            />
            <Space style={{ paddingTop: '1rem' }}>
              {!deleted && canEdit && <SaveButton />}
              <CancelButton onCancel={onCancel} />
            </Space>
          </FormWrapper.Single>
          <FormWrapper.Double>
            <Tabs>
              <Tabs.TabPane
                style={{ position: 'static' }}
                tab={
                  <span>
                    {t('entity.reports._plural')}
                    {errorMessages && errorMessages.reports && (
                      <Badge
                        count={1}
                        style={{ position: 'absolute', top: -25, right: -20 }}
                      />
                    )}
                  </span>
                }
                key="reports"
              >
                <Table
                  rowSelection={{
                    type: 'radio',
                    ...reportsSelection,
                    getCheckboxProps: () => ({
                      disabled: deleted || !canEdit,
                    }),
                  }}
                  columns={reportsColumns}
                  rowKey="uri"
                  pagination={reportsPagination}
                  dataSource={reportsTypes}
                  onChange={reportsHandleChange}
                />
              </Tabs.TabPane>
              <Tabs.TabPane
                style={{ position: 'static' }}
                tab={
                  <span>
                    {t('entity.taxonomy.councilDecision._plural')}
                    {errorMessages && errorMessages.councilDecisionIds && (
                      <Badge
                        count={1}
                        style={{ position: 'absolute', top: -25, right: -20 }}
                      />
                    )}
                  </span>
                }
                key="councilDecisionIds"
              >
                <Table
                  rowSelection={{
                    type: 'checkbox',
                    ...councilDecisionSelection,
                    getCheckboxProps: (record) => ({
                      disabled: record.deleted || deleted || !canEdit,
                    }),
                  }}
                  columns={councilDecisionColumns}
                  rowKey="id"
                  pagination={councilDecisionPagination}
                  dataSource={councilDecisionTypes}
                  onChange={councilDecisionHandleChange}
                />
              </Tabs.TabPane>
            </Tabs>
          </FormWrapper.Double>
        </FormWrapper>
      </form>
    </>
  );
};

export default EditCouncilDecisionForm;
